import { jwtDecode } from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { useIdp } from './auth/IdpProvider.js';

/**
 * Minimal interface describing our JWT payload.
 * - "exp" is the Unix timestamp when the token expires.
 */
interface JwtPayload {
  exp: number;
  name?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
}

/** Max time to wait for useRehydrateTokens to populate the store. */
const HYDRATION_TIMEOUT_MS = 2_000;

const displayNameFromClaims = (claims: JwtPayload): string => {
  const hasName = claims.name || [claims.given_name, claims.family_name].filter(Boolean).join(' ');
  return hasName || claims.preferred_username || 'User';
};

/**
 * Subscribe to the store and resolve as soon as a token appears,
 * or resolve null after HYDRATION_TIMEOUT_MS.
 */
const waitForToken = (): Promise<string | null> => {
  const immediate = useGlobalStore.getState().token;
  if (immediate) return Promise.resolve(immediate);

  return new Promise<string | null>((resolve) => {
    const timer = setTimeout(() => {
      unsub();
      resolve(null);
    }, HYDRATION_TIMEOUT_MS);

    const unsub = useGlobalStore.subscribe((state) => {
      if (state.token) {
        clearTimeout(timer);
        unsub();
        resolve(state.token);
      }
    });
  });
};

/**
 * Higher-order component that protects child routes.
 *
 * 1. Checks whether we have valid credentials (token, refreshToken, jti).
 * 2. Decodes the JWT access token to check expiration.
 * 3. If expired, attempts to refresh the token.
 * 4. If refresh fails (or tokens are missing), logs out and forces user to re-authenticate.
 * 5. If everything is okay, renders an <Outlet /> containing the protected routes.
 */
export function RequireAuth() {
  const location = useLocation();
  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);
  const logout = useGlobalStore((s: GlobalState) => s.logout);
  const login = useGlobalStore((s: GlobalState) => s.login);
  const setProfile = useGlobalStore((s: GlobalState) => s.setProfile);

  const idp = useIdp();

  const [initDone, setInitDone] = useState(false);

  useEffect(() => {
    // Handle OAuth callback fragment tokens, if present
    try {
      const hash = typeof window !== 'undefined' ? window.location.hash : '';
      if (hash && hash.includes('accessToken=')) {
        const params = new URLSearchParams(hash.replace(/^#/, ''));
        const at = params.get('accessToken');
        const rt = params.get('refreshToken');
        const jti = params.get('jti');
        if (at) {
          login(at, rt ?? undefined, jti ?? undefined, true);
          if (typeof window !== 'undefined') {
            window.history.replaceState(
              null,
              document.title,
              window.location.pathname + window.location.search,
            );
          }
        }
      }
    } catch {
      // ignore hash parse issues
    }

    const refreshFromServer = async (): Promise<string | null> => {
      const res = await idp.axios.post('/api/auth/refresh', {});
      const accessToken = res?.data?.accessToken;
      if (typeof accessToken === 'string' && accessToken.length > 0) {
        login(accessToken, res.data.refreshToken ?? undefined, res.data.jti ?? undefined, true);
        return accessToken;
      }
      return null;
    };

    const loadProfileFromApi = async () => {
      const me = await idp.authApi.authControllerGetProfile();
      const nameFromServer = me.data.displayName || me.data.fullName || 'User';
      const avatarUrl = (me.data as { avatar?: string }).avatar;
      setProfile?.({
        name: nameFromServer,
        email: me.data.email ?? '',
        ...(avatarUrl ? { avatarUrl } : {}),
      });
    };

    let cancelled = false;

    (async () => {
      try {
        // P2: Wait for useRehydrateTokens to hydrate the store (event-driven)
        let token = await waitForToken();
        if (cancelled) return;

        // Last resort: try a server-side refresh (covers cookie-mode edge cases)
        if (!token) {
          token = await refreshFromServer();
        }
        if (cancelled) return;

        // P1: No token after hydration + refresh → hard auth failure
        if (!token) {
          logout();
          return;
        }

        let claims = jwtDecode<JwtPayload>(token);
        const nowSec = Math.floor(Date.now() / 1000);

        if (claims.exp < nowSec) {
          const refreshed = await refreshFromServer();
          if (cancelled) return;
          if (!refreshed) {
            logout();
            return;
          }
          token = refreshed;
          claims = jwtDecode<JwtPayload>(token);
        }

        if (cancelled) return;

        const displayName = displayNameFromClaims(claims);
        if (displayName || claims.email) {
          setProfile?.({ name: displayName, email: claims.email || '' });
        }

        try {
          if (!cancelled) await loadProfileFromApi();
        } catch {
          // Non-fatal: keep token-derived profile
        }
      } catch {
        // If decoding or refresh fails, log out to prevent using an invalid token
        if (!cancelled) logout();
      } finally {
        if (!cancelled) setInitDone(true);
      }
    })();

    // P3: Cleanup — prevent state updates after unmount
    return () => {
      cancelled = true;
    };
  }, [logout, login, idp, setProfile]);

  if (!initDone) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return <Outlet />;
}
